import React, { useEffect, useState } from "react";
import { Button, Box, Typography, Alert, Card, CardContent, Chip, Snackbar } from "@mui/material";
import { 
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import { userUpdate } from "../../graphql";
import { CURRENT_USER_QUERY, useAuthContext } from "../auth";
import { useMutation } from "@apollo/client";
import { getGmailAccessToken, debugOAuthToken, isOAuthConfigured, isEmailConfigured } from "../../services/emailService";

const GoogleConfig = () => {
  const { user } = useAuthContext();
  const [isConfigured, setIsConfigured] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [UpdateUser, { loading, data }] = useMutation(userUpdate, {
    refetchQueries: [
      {
        query: CURRENT_USER_QUERY,
      },
    ],
  });

  // Gmail status state
  const [gmailStatus, setGmailStatus] = useState({
    isConfigured: false,
    userEmail: null,
    lastConnected: null,
    tokenStatus: 'checking'
  });

  const get_sms_access_token = localStorage.getItem("ring_central_token");
  const get_sms_refresh_token = localStorage.getItem("ring_central_refresh_token");

  useEffect(() => {
    if (!loading && data) {
      window.location.reload();
    }
  }, [loading, data]);

  // Note: OAuth callback is now handled by the dedicated GmailCallback page

  useEffect(() => {
    const gmailAccessToken = localStorage.getItem("gmail_token");
    const wasConfigured = isConfigured;
    const isNowConfigured = !!gmailAccessToken;
    
    setIsConfigured(isNowConfigured);
    
    // Show success message if newly configured
    if (!wasConfigured && isNowConfigured) {
      setShowSuccessMessage(true);
    }
    
    // Run debug check on mount
    const info = debugOAuthToken();
    setDebugInfo(info);
    console.log('OAuth Debug Info:', info);
  }, []);

  // Check Gmail configuration status with enhanced verification
  useEffect(() => {
    const checkGmailStatus = () => {
      const isConfigured = isEmailConfigured('gmail');
      const userData = localStorage.getItem('gmail_user_data');
      const accessToken = localStorage.getItem('gmail_token');
      const refreshToken = localStorage.getItem('gmail_refresh_token');
      
      let userEmail = 'Gmail Account';
      let lastConnected = null;
      let tokenStatus = 'checking';
      
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          userEmail = parsedData.email || 'Gmail Account';
          lastConnected = parsedData.lastConnected || new Date().toISOString();
        } catch (e) {
          console.error('Error parsing Gmail user data:', e);
        }
      }
      
      if (accessToken && refreshToken) {
        tokenStatus = 'valid';
      } else if (accessToken && !refreshToken) {
        tokenStatus = 'partial';
      } else if (!accessToken && !refreshToken) {
        tokenStatus = 'none';
      }
      
      setGmailStatus({ 
        isConfigured, 
        userEmail,
        lastConnected,
        tokenStatus
      });
    };
    
    checkGmailStatus();
    // Check every 5 seconds for changes
    const interval = setInterval(checkGmailStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Listen for storage changes to detect when OAuth is completed
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'gmail_token' && e.newValue) {
        console.log('🎉 Gmail token detected in localStorage!');
        setIsConfigured(true);
        setShowSuccessMessage(true);
        // Refresh debug info
        const info = debugOAuthToken();
        setDebugInfo(info);
      }
    };

    // Listen for custom Gmail connection event
    const handleGmailConnected = (e) => {
      console.log('🎉 Gmail connection event received:', e.detail);
      setIsConfigured(true);
      setShowSuccessMessage(true);
      
      // Update status with new user data
      if (e.detail.userData) {
        setGmailStatus(prev => ({
          ...prev,
          userEmail: e.detail.userData.email || 'Gmail Account',
          lastConnected: e.detail.timestamp,
          tokenStatus: 'valid'
        }));
      }
      
      // Refresh debug info
      const info = debugOAuthToken();
      setDebugInfo(info);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('gmailConnected', handleGmailConnected);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('gmailConnected', handleGmailConnected);
    };
  }, []);

  const handleGmailLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/callback`;
    const scope = 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/userinfo.email';
    const state = Math.random().toString(36).substring(7);
    
    // Store state for verification
    localStorage.setItem('gmail_oauth_state', state);
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${state}&access_type=offline&prompt=consent`;
    
    console.log('🔐 Starting Gmail OAuth with:');
    console.log('Client ID:', clientId ? 'Present' : 'Missing');
    console.log('Redirect URI:', redirectUri);
    console.log('Scope:', scope);
    console.log('State:', state);
    console.log('Full Auth URL:', authUrl);
    
    // Open OAuth in new window to keep user on settings page
    window.open(authUrl, '_blank');
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const getTokenStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'success';
      case 'partial': return 'warning';
      case 'none': return 'error';
      default: return 'default';
    }
  };

  const getTokenStatusText = (status) => {
    switch (status) {
      case 'valid': return 'Valid';
      case 'partial': return 'Partial';
      case 'none': return 'None';
      default: return 'Checking...';
    }
  };

  // Function to verify token persistence
  const verifyTokenPersistence = () => {
    const accessToken = localStorage.getItem('gmail_token');
    const refreshToken = localStorage.getItem('gmail_refresh_token');
    const userData = localStorage.getItem('gmail_user_data');
    const tokenExpiry = localStorage.getItem('gmail_token_expiry');
    
    const now = new Date();
    const expiryDate = tokenExpiry ? new Date(tokenExpiry) : null;
    const isExpired = expiryDate && expiryDate < now;
    
    console.log('🔍 Token persistence verification:');
    console.log('  - Access Token:', accessToken ? 'Present' : 'Missing');
    console.log('  - Refresh Token:', refreshToken ? 'Present' : 'Missing');
    console.log('  - User Data:', userData ? 'Present' : 'Missing');
    console.log('  - Token Expiry:', expiryDate ? expiryDate.toISOString() : 'Not set');
    console.log('  - Is Expired:', isExpired);
    
    return {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      hasUserData: !!userData,
      isExpired,
      expiryDate
    };
  };

  // Note: Token exchange is now handled by the GmailCallback page

  return (
    <Box sx={{ width: '100%' }}>
      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccessMessage} 
          severity="success" 
          sx={{ width: '100%' }}
          icon={<CheckCircleIcon />}
        >
          <Typography variant="h6" gutterBottom>
            🎉 Gmail Successfully Connected & Configured!
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ✅ <strong>OAuth Authentication:</strong> Completed successfully
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ✅ <strong>Access Token:</strong> Securely stored in browser
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ✅ <strong>Refresh Token:</strong> Available for automatic renewal
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ✅ <strong>User Account:</strong> {gmailStatus.userEmail || 'Gmail Account'} verified
          </Typography>
          <Typography variant="body2">
            🚀 <strong>Ready to use:</strong> You can now send emails through the system!
          </Typography>
        </Alert>
      </Snackbar>

      {/* Gmail Configuration Status Indicator */}
      <Card sx={{ 
        p: 3, 
        py: 1.5,
        mb: 3,
        bgcolor: gmailStatus.isConfigured ? 'success.50' : 'warning.50',
        border: `2px solid ${gmailStatus.isConfigured ? '#10b981' : '#f59e0b'}`,
        borderRadius: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {gmailStatus.isConfigured ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 36 }} />
          ) : (
            <WarningIcon color="warning" sx={{ fontSize: 36 }} />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              color: gmailStatus.isConfigured ? 'success.main' : 'warning.main',
              mb: 1
            }}>
              {gmailStatus.isConfigured ? '🎉 Gmail Fully Configured & Working!' : '⚠️ Gmail Not Configured'}
            </Typography>
            
            {gmailStatus.isConfigured && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip 
                  icon={<EmailIcon />} 
                  label={gmailStatus.userEmail} 
                  color="success" 
                  variant="outlined"
                  size="small"
                />
                <Chip 
                  icon={<SecurityIcon />} 
                  label={`Token: ${getTokenStatusText(gmailStatus.tokenStatus)}`}
                  color={getTokenStatusColor(gmailStatus.tokenStatus)}
                  variant="outlined"
                  size="small"
                />
                {gmailStatus.lastConnected && (
                  <Chip 
                    icon={<RefreshIcon />} 
                    label={`Connected: ${new Date(gmailStatus.lastConnected).toLocaleDateString()}`}
                    color="info"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {/* Connection Status Details */}
      {gmailStatus.isConfigured && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ✅ Gmail Connection Verified & Tokens Securely Stored
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Account:</strong> {gmailStatus.userEmail}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Access Token:</strong> ✅ Securely stored and valid
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Refresh Token:</strong> ✅ Available for automatic renewal
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Permissions:</strong> ✅ Gmail send, compose, and user info access granted
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>Token Status:</strong> <Chip 
              label={getTokenStatusText(gmailStatus.tokenStatus)} 
              color={getTokenStatusColor(gmailStatus.tokenStatus)} 
              size="small" 
              variant="outlined"
            />
          </Typography>
          <Typography variant="body2">
            • <strong>Last Connected:</strong> {gmailStatus.lastConnected ? 
              new Date(gmailStatus.lastConnected).toLocaleString() : 'Recently'
            }
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'end' }}>
        <Button variant="contained" onClick={handleGmailLogin}>
          {isConfigured ? "Reconfigure Gmail" : "Connect Gmail"}
        </Button>
        {isConfigured && (
          <Button 
            variant="outlined" 
            onClick={verifyTokenPersistence}
            startIcon={<SecurityIcon />}
            color="info"
          >
            Verify Tokens
          </Button>
        )}
        <Button 
          variant="outlined" 
          onClick={() => setShowDebug(!showDebug)}
          startIcon={<SettingsIcon />}
        >
          {showDebug ? "Hide Debug" : "Show Debug"}
        </Button>
      </Box>

      {showDebug && debugInfo && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              OAuth Debug Information
            </Typography>
            
            <Alert severity={isOAuthConfigured('gmail') ? "success" : "warning"} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Configuration:</strong> {isOAuthConfigured('gmail') ? "OK" : "Missing environment variables"}
              </Typography>
            </Alert>

            <Typography variant="subtitle2" gutterBottom>
              Environment Variables:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Client ID: {debugInfo.hasClientId ? "✅ Present" : "❌ Missing"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Client Secret: {debugInfo.hasClientSecret ? "✅ Present" : "❌ Missing"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              • Live URL: {debugInfo.hasLiveUrl ? `✅ ${debugInfo.redirectUri}` : "❌ Missing"}
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              Token Status:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Access Token: {debugInfo.hasToken ? "✅ Present" : "❌ Missing"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Refresh Token: {debugInfo.hasRefreshToken ? "✅ Present" : "❌ Missing"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              • User Data: {debugInfo.hasUserData ? "✅ Present" : "❌ Missing"}
            </Typography>

            {debugInfo.authCode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Auth Code Found:</strong> {debugInfo.authCode.substring(0, 20)}...
                </Typography>
              </Alert>
            )}

            {debugInfo.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>OAuth Error:</strong> {debugInfo.error}
                </Typography>
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              Check browser console for detailed logs (F12 → Console)
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GoogleConfig;
